import {
  AccountBalance,
  AdminPanelSettings,
  ExpandLess,
  ExpandMore,
  HomeOutlined,
  LocalShippingOutlined,
  PowerSettingsNew,
  ShoppingCartCheckout,
  Store,
  SupportAgent,
  VolunteerActivism as VolunteerActivismIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText as MuiListItemText,
  ListItemTextProps,
  styled,
  Typography,
} from "@mui/material";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import TokenService from "services/token.service";
import { userHasPermission } from "utils/userPermissions";
import "./assets/App.css";
import { api } from "services/api.service";

const ListItemText = styled(MuiListItemText)<ListItemTextProps>(() => ({
  ".MuiTypography-root": { fontWeight: "bold" },
}));

interface ListItemCollapseProps {
  label: React.ReactNode;
  icon: React.ReactNode;
  items: {
    label: React.ReactNode;
    to: string;
    hidden?: boolean;
  }[];

  onClick(): void;

  open: boolean;
}

export function isNavItemSelected(itemTo: string, pathname: string) {
  const itemSegments = itemTo.split("?")[0].split("/").filter(Boolean);
  const pathSegments = pathname.split("?")[0].split("/").filter(Boolean);
  const depth = itemTo.split("/").length == 3 ? 2 : 1;
  if (itemSegments.length < depth || pathSegments.length < depth) return false;
  return itemSegments.slice(0, depth).every((segment, index) => pathSegments[index] === segment);
}

function ListItemCollapse({ onClick, open, label, icon, items }: ListItemCollapseProps) {
  const { pathname } = useLocation();

  let selectedItemIndex = -1;
  items.forEach((item, i) => {
    if (isNavItemSelected(item.to, pathname)) {
      selectedItemIndex = i;
    }
  });
  const hasSelectedItem = selectedItemIndex > -1;

  const [isOpen, setIsOpen] = useState(() => hasSelectedItem && open);

  useEffect(() => {
    if (!open) {
      setIsOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (hasSelectedItem && open) {
      setIsOpen(true);
    }
  }, [hasSelectedItem, open]);

  return (
    <>
      <ListItemButton
        onClick={() => {
          setIsOpen(!isOpen);
          onClick();
        }}
      >
        <ListItemIcon sx={{ minWidth: 0 }}>{icon}</ListItemIcon>
        <ListItemText primary={label} />
        {isOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <Box
          sx={{
            my: 1,
            ml: 4,
            pt: 1,
            px: 2,
            borderLeft: `2px solid #BDBDBD`,
            ".MuiButtonBase-root": {
              borderRadius: "4px",
              color: "text.secondary",
              ".MuiTypography-root": { fontWeight: "normal" },
              "&.Mui-selected": { borderRight: `0` },
            },
          }}
        >
          <List component="div" disablePadding>
            {items.map((item, i) => {
              const isSelected = selectedItemIndex === i;
              if (item.hidden) return null;
              return (
                <ListItemButton key={i} onClick={onClick} selected={isSelected} component={Link} to={item.to}>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      </Collapse>
    </>
  );
}

const logout = async () => {
  try {
    await api.delete("/auth/sign_out");
  } finally {
    TokenService.remove("accessToken");
    window.location.href = "/login";
  }
};

const useLogoutConfirmation = () => {
  const [windowOpenState, setWindowOpenState] = useState(false);

  const handleOpenLogoutConfirmationWindow = () => {
    setWindowOpenState(true);
  };

  const handleCloseLogoutConfirmationWindow = () => {
    setWindowOpenState(false);
  };

  function LogoutConfirmationDialog() {
    return (
      <Dialog open={windowOpenState} onClose={handleCloseLogoutConfirmationWindow} fullWidth>
        <DialogContent>
          <Typography variant="body1">Are you sure you want to log out?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogoutConfirmationWindow}>Cancel</Button>
          <Button onClick={logout}>Log Out</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return { LogoutConfirmationDialog, handleOpenLogoutConfirmationWindow };
};

interface ListItemsProps {
  open: boolean;

  setOpen(v: boolean): void;

  currentUserPermissions: string[];
}

export function ListItems({ open, setOpen, currentUserPermissions }: ListItemsProps) {
  const { LogoutConfirmationDialog, handleOpenLogoutConfirmationWindow } = useLogoutConfirmation();
  const { enabled: adminSectionFeature } = useFeatureFlag("admin_section");
  const { enabled: ncpOrdersFeature } = useFeatureFlag("ncp_orders");
  const { enabled: boomerangSupportFeature } = useFeatureFlag("internal_support_boomerang");
  const { enabled: inventoryPartsManagementFeature } = useFeatureFlag("inventory_parts_management");
  const { enabled: newLogisticsPageFeature } = useFeatureFlag("new_logistics_page_enabled");

  return (
    <List component={Box}>
      {userHasPermission("warehouse", currentUserPermissions) && (
        <ListItemCollapse
          label={"Warehouse"}
          icon={<HomeOutlined color="primary" />}
          open={open}
          onClick={() => {
            setOpen(true);
          }}
          items={[
            {
              label: "Pickups",
              to: "/pickups",
            },
            {
              label: "Inventory",
              to: "/inventories?q=state%3A%28in_process+OR+resellable%29",
            },
            {
              label: "Inventory Templates",
              to: "/inventory-templates",
            },
            { label: "Inventory Parts", to: "/inventory-parts", hidden: !inventoryPartsManagementFeature },
            {
              label: "Inventory Uploads",
              to: "/inventory-uploads",
            },
            {
              label: "Locations",
              to: "/locations",
            },
            {
              label: "Tech",
              to: "/technician",
            },
            {
              label: "Move Item",
              to: "/scan",
            },
            {
              label: "Outbound",
              to: "/outbound",
            },
            {
              label: "Drivers",
              to: "/drivers",
            },
            {
              label: "Scan & Discover",
              to: "/scan-and-discover",
            },
            {
              label: "Inbound Shipping",
              to: "/virtual-pickup?virtual_pending_processing=true",
            },
            {
              label: "Replacement Finder",
              to: "/replacement-finder",
            },
          ]}
        />
      )}
      {userHasPermission("warehouse", currentUserPermissions) && (
        <ListItemCollapse
          label={"Fulfillment"}
          icon={<ShoppingCartCheckout color="primary" />}
          open={open}
          onClick={() => setOpen(true)}
          items={[
            {
              label: "Picklist",
              to: "/picklist",
            },
            {
              label: "Boomerang",
              to: "/asset_retrievals?asset_retrieval_status=open",
              hidden: boomerangSupportFeature,
            },
            {
              label: "Boomerang",
              to: "/boomerang",
              hidden: !boomerangSupportFeature,
            },
            {
              label: "Orders",
              to: "/orders/due-today",
            },
            {
              label: "NCP Orders",
              to: "/ncp-orders/due-today",
              hidden: !ncpOrdersFeature,
            },
            {
              label: "Bulk Orders",
              to: "/bulk-orders/wholesale",
            },
            {
              label: "Batches",
              to: "/batches?sort=-age",
            },
          ]}
        />
      )}
      {userHasPermission("partner_warehouse", currentUserPermissions) &&
        !userHasPermission("warehouse", currentUserPermissions) && (
          <ListItemCollapse
            label={"Warehouse"}
            icon={<HomeOutlined color="primary" />}
            open={open}
            onClick={() => {
              setOpen(true);
            }}
            items={[
              {
                label: "Pickups",
                to: "/pickups",
              },
              {
                label: "Inventory",
                to: "/inventories?filter%5B%5D=state%2Cis_any_of%2Cin_process%2Cresellable",
              },
              {
                label: "Inventory Templates",
                to: "/inventory-templates",
              },
              {
                label: "Inventory Uploads",
                to: "/inventory-uploads",
              },
              {
                label: "Locations",
                to: "/locations",
              },
              {
                label: "Tech",
                to: "/technician",
              },
              {
                label: "Move Item",
                to: "/scan",
              },
              {
                label: "Outbound",
                to: "/outbound",
              },
              {
                label: "Scan & Discover",
                to: "/scan-and-discover",
              },
            ]}
          />
        )}

      {userHasPermission("logistics", currentUserPermissions) && (
        <ListItemCollapse
          label={"Logistics"}
          icon={<LocalShippingOutlined color="primary" />}
          open={open}
          onClick={() => setOpen(true)}
          items={[
            {
              label: "Pickups",
              to: "/logistics?logistics_status=scheduled",
            },
            {
              label: "Logistics Platform",
              to: "/logistics_platform",
              hidden: !newLogisticsPageFeature,
            },
            {
              label: "Partners",
              to: "/logistics/partners",
            },
            {
              label: "Clients",
              to: "/logistics/clients",
            },
          ]}
        />
      )}

      {userHasPermission("finance", currentUserPermissions) && (
        <ListItemCollapse
          label={"Finance"}
          icon={<AccountBalance color="primary" />}
          open={open}
          onClick={() => setOpen(true)}
          items={[
            {
              label: "Pickups",
              to: "/finance/pickups",
            },
            {
              label: "Clients",
              to: "/finance/clients",
            },
          ]}
        />
      )}

      {userHasPermission("tech_access", currentUserPermissions) && (
        <ListItemCollapse
          label={"Tech Access"}
          icon={<VolunteerActivismIcon color="primary" />}
          open={open}
          onClick={() => setOpen(true)}
          items={[
            {
              label: "Donation Requests",
              to: "/donation-requests",
            },
            {
              label: "Benefiting Organizations",
              to: "/benefiting-organization",
            },
          ]}
        />
      )}
      {userHasPermission("support", currentUserPermissions) && (
        <ListItemCollapse
          label={"Support"}
          icon={<SupportAgent color="primary" />}
          open={open}
          onClick={() => setOpen(true)}
          items={[
            {
              label: "Clients",
              to: "/support/clients",
            },
            {
              label: "Boomerang",
              to: "/support/asset_retrievals?asset_retrieval_status=open",
              hidden: !boomerangSupportFeature,
            },
          ]}
        />
      )}
      {userHasPermission("resale", currentUserPermissions) && (
        <ListItemCollapse
          label={"Resale"}
          icon={<Store color="primary" />}
          open={open}
          onClick={() => setOpen(true)}
          items={[
            {
              label: "Product Catalog",
              to: "/product-catalog",
            },
            {
              label: "Upload Stock Photos",
              to: "/stock-photos",
            },
            {
              label: "Listings",
              to: "/listings",
            },
            {
              label: "Photo Booth",
              to: "/resale-photo-booth",
            },
          ]}
        />
      )}
      {adminSectionFeature && userHasPermission("admin", currentUserPermissions) && (
        <ListItemCollapse
          label={"Admin"}
          icon={<AdminPanelSettings color="primary" />}
          open={open}
          onClick={() => setOpen(true)}
          items={[
            {
              label: "Inventory Group Rules",
              to: "/inventory-group-rules",
            },
            {
              label: "Location Types",
              to: "/location-types",
            },
            {
              label: "Location Kinds",
              to: "/location-kinds",
            },
            {
              label: "Movement Actions",
              to: "/movement-actions",
            },
            {
              label: "Allowed Movements",
              to: "/allowed-movements",
            },
            {
              label: "Kind Movements",
              to: "/kind-movements",
            },
            {
              label: "Movement Conditions",
              to: "/location-type-movement-conditions",
            },
          ]}
        />
      )}
      <LogoutConfirmationDialog />
      <ListItemButton onClick={handleOpenLogoutConfirmationWindow}>
        <ListItemIcon sx={{ minWidth: 0 }}>
          <PowerSettingsNew color="primary" />
        </ListItemIcon>
        <ListItemText primary="Log Out" />
      </ListItemButton>
    </List>
  );
}
